import { useCallback } from "react";
import { toast } from "sonner";

// ─── Hook config & return ────────────────────────────────────────────────────

export interface UseArchiveActionsConfig {
  /** Archive a single item by ID. Should throw on failure. */
  archiveFn: (id: number) => Promise<void>;
  /** Unarchive a single item by ID. Should throw on failure. */
  unarchiveFn: (id: number) => Promise<void>;
  /** Singular noun for toast messages (e.g. "submission", "application"). */
  itemLabel: string;
  /** Called after a successful archive/unarchive to refresh server data. */
  onSuccess?: () => void;
  /** Called to reset row selection state after bulk operations. */
  onResetSelection?: () => void;
}

export interface UseArchiveActionsReturn {
  /** Archive a single item. Shows error toast on failure. */
  handleArchive: (id: number) => Promise<void>;
  /** Unarchive a single item. Shows error toast on failure. */
  handleUnarchive: (id: number) => Promise<void>;
  /** Archive multiple items. Shows success/error toast and resets selection. */
  handleBulkArchive: (ids: number[]) => Promise<void>;
  /** Unarchive multiple items. Shows success/error toast and resets selection. */
  handleBulkUnarchive: (ids: number[]) => Promise<void>;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useArchiveActions({
  archiveFn,
  unarchiveFn,
  itemLabel,
  onSuccess,
  onResetSelection,
}: UseArchiveActionsConfig): UseArchiveActionsReturn {
  const handleArchive = useCallback(
    async (id: number) => {
      try {
        await archiveFn(id);
        onSuccess?.();
      } catch {
        toast.error(`Failed to archive ${itemLabel}`);
      }
    },
    [archiveFn, itemLabel, onSuccess],
  );

  const handleUnarchive = useCallback(
    async (id: number) => {
      try {
        await unarchiveFn(id);
        onSuccess?.();
      } catch {
        toast.error(`Failed to unarchive ${itemLabel}`);
      }
    },
    [unarchiveFn, itemLabel, onSuccess],
  );

  const handleBulkArchive = useCallback(
    async (ids: number[]) => {
      const results = await Promise.allSettled(ids.map(archiveFn));
      const failures = results.filter((r) => r.status === "rejected");

      if (failures.length > 0) {
        toast.error(
          `Failed to archive ${failures.length} ${itemLabel}${failures.length === 1 ? "" : "s"}`,
        );
      } else {
        toast.success(
          `${ids.length} ${itemLabel}${ids.length === 1 ? "" : "s"} archived`,
        );
      }

      onResetSelection?.();
      onSuccess?.();
    },
    [archiveFn, itemLabel, onSuccess, onResetSelection],
  );

  const handleBulkUnarchive = useCallback(
    async (ids: number[]) => {
      const results = await Promise.allSettled(ids.map(unarchiveFn));
      const failures = results.filter((r) => r.status === "rejected");

      if (failures.length > 0) {
        toast.error(
          `Failed to unarchive ${failures.length} ${itemLabel}${failures.length === 1 ? "" : "s"}`,
        );
      } else {
        toast.success(
          `${ids.length} ${itemLabel}${ids.length === 1 ? "" : "s"} unarchived`,
        );
      }

      onResetSelection?.();
      onSuccess?.();
    },
    [unarchiveFn, itemLabel, onSuccess, onResetSelection],
  );

  return {
    handleArchive,
    handleUnarchive,
    handleBulkArchive,
    handleBulkUnarchive,
  };
}
